import React, { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { themeColor } from '../../../Theming';

interface DropDownProps extends PropsWithChildren {
  items: DropDownItemProps[];
}

const StyledTooltip = styled.div`
  width: 10px;
  height: 10px;
  border-top: 1px solid ${themeColor('lightGreen')};
  border-left: 1px solid ${themeColor('lightGreen')};
  background-color: ${themeColor('white')};
  position: absolute;
  top: -11px;
  left: 10px;
  transform-origin: 0 100%;
  transform: rotate(45deg);
`;

const sharedStyling = css`
  color: ${themeColor('darkestGrey')};
  font-size: 0.875rem;
  line-height: 1.5rem;
  text-decoration: none;
  overflow: hidden;
  flex-shrink: 0;
  text-align: center;
  font-weight: 700;

  &.selected::after {
    content: '';
    width: 72px;
    height: 3px;
    border-radius: 1.5px;
    background: ${themeColor('darkestGrey')};
    margin: 10px auto 0;
    display: block;
  }
  &:focus {
    color: ${themeColor('darkestGrey')};
    text-decoration: none;
  }
`;

const StyledLink = styled(Link)`
  ${sharedStyling}
  text-align: unset;
`;

const StyledDropdownLink = styled(StyledLink)`
  padding: 5px 20px;
  font-weight: 400;
`;

const StyledDropdownContainer = styled.div`
  color: ${themeColor('darkestGrey')};
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: 700;
  height: 90px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: ${themeColor('lightGreen')};
  }
`;

const DropdownContainer = styled.div`
  width: 177px;
  min-height: 30px;
  position: absolute;
  top: 76px;
  border: 1px solid ${themeColor('lightGreen')};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  border-radius: 4px;
  padding: 20px 0;
  background-color: ${themeColor('white')};
`;

export interface DropDownItemProps {
  text: string;
  link: string;
}

const DropDownMenu: FC<DropDownProps> = ({ items, children }) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);

  const handleMouseEnter = () => {
    setShowMenu(true);
  };

  const handleMouseLeave = () => {
    setShowMenu(false);
  };

  return (
    <StyledDropdownContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showMenu && (
        <DropdownContainer>
          {items.map((item, key) => (
            <StyledDropdownLink key={key} to={item.link}>
              {t(item.text)}
            </StyledDropdownLink>
          ))}
          <StyledTooltip />
        </DropdownContainer>
      )}
    </StyledDropdownContainer>
  );
};

export default DropDownMenu;
